import React, { useState, useEffect, useContext } from 'react';
import { format, isBefore, isSameDay, parseISO, set } from 'date-fns';
import { Modal, Form, Row, Col, InputGroup, Spinner, Button } from 'react-bootstrap';
import { FaUser } from 'react-icons/fa';
import { Oval } from 'react-loader-spinner';
import { useAuth } from '../../contexts/auth-context';
import { useNavigate } from 'react-router-dom';
import { NotificationContext } from '../../contexts/notification-context';
import agendamentosService from '../../api/agendamentosService';
import salasService from '../../api/salasService';
import horariosService from '../../api/horariosService'; 
import './index.css';
import { PERFIS } from '../../constants/perfis';

const CalendarGrid = ({ selectedDate }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { addNotification } = useContext(NotificationContext); 
  const [agendamentos, setAgendamentos] = useState([]);
  const [salas, setSalas] = useState([]); 
  const [horarios, setHorarios] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState();
  const [tipoAgendamento, setTipoAgendamento] = useState(true);
  const [formData, setFormData] = useState({
    nome: '',
    avulso: true,
  });
  const [podeCancelar, setPodeCancelar] = useState(false);

  const fetchSalasHorarios = async () => {
    try {
      setLoading(true);
      const salasResponse = await salasService.listar();
      const horariosResponse = await horariosService.listar();
      setSalas(salasResponse);
      setHorarios(horariosResponse);
    } catch (err) {
      console.error("Erro ao buscar salas e horários:", err);
      addNotification("danger", "Erro ao carregar salas e horários.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAgendamentos = async () => {
    setLoading(true);
    setError(null);
    try {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      const response = await agendamentosService.listar(formattedDate);
      setAgendamentos(Array.isArray(response) ? response : []);
    } catch (err) {
      addNotification('danger','Erro ao buscar agendamentos.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalasHorarios(); 
    fetchAgendamentos(); 
  }, [selectedDate]);

  const temMais24Horas = (date) => {
    const inputDate = typeof date === 'string' ? new Date(date) : date;

    if (!inputDate || !(inputDate instanceof Date) || isNaN(inputDate.getTime())) {
      return false;
    }

    const now = new Date();
    const diffInMs = inputDate.getTime() - now.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);

    return diffInHours >= 24;
  };

  const handleCellClick = (sala, horario) => {
    const now = new Date();
    const horarioCompleto = set (selectedDate, {
      hours: parseInt(horario.horario.split(':')[0], 10),
      minutes: parseInt(horario.horario.split(':')[1], 10),
    });

    if (isBefore (horarioCompleto, now)) {
      addNotification('warning', 'Este horário já passou e não pode ser agendado.');
      return;
    }

    setPodeCancelar(temMais24Horas(horarioCompleto));
    
    const agendamento = agendamentos?.find(
      (ag) =>
        ag.sala.id === sala.id &&
        ag.horario.id === horario.id &&
        isSameDay(parseISO(ag.data), selectedDate)
    );

    if (agendamento) {
      if (user.perfilId === PERFIS.ADMINISTRADOR || agendamento.profissionalId === user.profissionalId) {
        setModalData({
          avulso: agendamento.avulso,
          agendamento,
        });
        setFormData({
          nome: agendamento.profissional?.nome || '',
          avulso: agendamento.avulso,
        });
        setTipoAgendamento(agendamento.avulso);
        setShowModal(true);
      } else {
        return;
      }
    } else {
      if (!user) {
        alert('Você precisa estar logado para agendar uma sala.');
        navigate('/login');
      } else {
        setFormData({
          nome: user.nome || '',
          avulso: true,
        });

        setModalData({
          avulso: 'livre',
          dia: selectedDate,
          sala,
          horario,
        });
        setTipoAgendamento(true);
        setShowModal(true);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalData(null);
    setTipoAgendamento(true);
  };

  const handleSubmitAgendamento = async (e) => {
    e.preventDefault();
    const { dia, sala, horario } = modalData;

    const dataCompleta = new Date(dia);
    dataCompleta.setHours(horario.hora);
    dataCompleta.setMinutes(horario.minuto);

    const novoAgendamento = {
      salaId: sala.id, 
      profissionalId: user.profissionalId,
      modalidadeId: 1,
      data: format(dataCompleta, "yyyy-MM-dd'T'HH:mm:ss"),
      horarioId: horario.id,
      avulso: tipoAgendamento === true,
      particular: true
    };
    
    try {
      await agendamentosService.inserir(novoAgendamento);
      fetchAgendamentos();
      handleCloseModal();
    } catch (err) {
      addNotification("danger", "Erro ao criar agendamento. Por favor, tente novamente.");
      console.error('Erro ao criar agendamento:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleTipoChange = (e) => {
    const novoTipo = e.target.value === 'true';
    setTipoAgendamento(novoTipo);
    setFormData((prevData) => ({
      ...prevData,
      avulso: novoTipo,
    }));
  };

  const handleCancelAgendamento = async () => {
    const { agendamento } = modalData;
    const agendamentosOriginais = [...agendamentos];
    setAgendamentos(agendamentos.filter(ag => ag.id !== agendamento.id));

    try {
      await agendamentosService.cancelar(agendamento.id, user.id);
      addNotification('success','Agendamento cancelado com sucesso.');
      fetchAgendamentos();
      handleCloseModal();
    } catch (err) {
      console.error('Erro ao cancelar agendamento:', err);
      addNotification('danger','Erro ao cancelar agendamento. Por favor, tente novamente.');
      setAgendamentos(agendamentosOriginais);
    }
  };

  return (
    <div className="calendar-grid-container" style={{ overflowX: 'auto' }}>
      {loading ? (
        <div className="loading">
          <Oval
            height={40}
            width={40}
            color="#007bff"
            visible={true}
            ariaLabel="oval-loading"
          />
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <table className="custom-table" >
          <thead>
            <tr>
              <th className="header-cell first-column">#</th>
              {salas.length > 0 ? 
                (salas.map((sala, index) => (
                  <th key={index} className="text-center header-cell">
                    {sala.nome}
                  </th>
                ))): 
                (<th key={1} className="text-center header-cell">Sala</th>)}
            </tr>
          </thead>
          <tbody>
            {horarios.length > 0 ? (
              horarios.map((horarioObj, index) => {
                return (
                  <tr key={index}>
                    <td className="align-middle text-center fw-bold first-column">
                      {parseInt(horarioObj.horario.split(':')[0], 10)}h
                    </td>
                    {salas.map((sala, idx) => {
                      const agendamento = agendamentos?.find(
                        (ag) =>
                          ag.sala.id === sala.id &&
                          ag.horario.id === horarioObj.id &&
                          isSameDay(parseISO(ag.data), selectedDate)
                      );

                      const isDisabled = (user.perfilId === PERFIS.ADMINISTRADOR && !agendamento) || 
                                         (user.perfilId === PERFIS.AGENDAMENTO && !agendamento) || 
                                         (user.perfilId === PERFIS.PROFISSIONAL && agendamento && agendamento.profissionalId !== user.profissionalId);
                    return (
                        <td
                          key={idx}
                          className={`align-middle text-center ${isDisabled ? 'disabled-cell' : ''}`}
                          style={{ cursor: isDisabled ? 'default' : 'pointer' }}
                          onClick={() => !isDisabled && handleCellClick(sala, horarioObj)}
                        >
                          {agendamento ? (
                            <>
                              <div>{agendamento.profissional?.nome}</div>
                              {agendamento.particular === false && 
                                agendamento.paciente &&
                                (user.perfilId === PERFIS.ADMINISTRADOR ||  user.perfilId === PERFIS.AGENDAMENTO) &&
                                 <div style={{color:'rgb(132, 0, 39)', fontSize:'14px'}}><b>({agendamento.paciente?.nome})</b></div>}
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: agendamento.avulso === false ? 'rgb(132, 0, 39)' : 'rgb(217, 188, 193)',
                                  color: agendamento.avulso === false ? 'white' : 'black',
                                  marginTop: '5px',
                                  display: 'inline-block',
                                  padding: '5px 10px',
                                }}
                              >
                                {agendamento.avulso === true ? "AVULSO" : "FIXO"}
                              </span>
                              {agendamento.particular === false &&
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: 'rgb(206, 186, 189)',
                                  color: 'black',
                                  marginTop: '5px',
                                  display: 'inline-block',
                                  padding: '5px 10px',
                                }}
                              >
                              HUMANIZE
                              </span>}
                            </>
                          ) : (
                            <span>Livre</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={salas.length + 2} className="text-center">
                  Nenhum horário disponível.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <Modal show={showModal} onHide={handleCloseModal} style={{ paddingTop: '65px' }}>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalData?.agendamento ? "Cancelar Agendamento" : "Novo Agendamento"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={modalData?.agendamento ? handleCancelAgendamento : handleSubmitAgendamento}>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="formNome">
                <InputGroup>
                  <InputGroup.Text><FaUser /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    placeholder="Nome do Profissional"
                    readOnly
                  />
                </InputGroup>
              </Form.Group>
            </Row>
            <Row className="mb-3">
                <Form.Group controlId="tipoAgendamento">
                  <Form.Label>Tipo de Agendamento</Form.Label>
                  <Form.Control
                    as="select"
                    className="form-select"
                    name="tipoAgendamento"
                    disabled={modalData?.agendamento}
                    value={tipoAgendamento}
                    onChange={handleTipoChange}
                  >
                    <option value={true}>Avulso</option>
                    <option value={false}>Fixo</option>
                  </Form.Control>
                </Form.Group>
            </Row>
            <div className="d-flex justify-content-end">
              <Button
                style={{
                  backgroundColor: 'rgb(132, 0, 39)',
                  border: 'none',
                  color: 'white',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s',
                }}
                type="button"
                disabled={modalData?.agendamento ? !podeCancelar : false}
                onClick={modalData?.agendamento ? handleCancelAgendamento : handleSubmitAgendamento}
              >
                {loading ? (
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                ) : (
                  modalData?.agendamento ? "Cancelar Agendamento" : "Agendar"
                )}
              </Button>
            </div>
          </Form>
            <div style={{ color: 'rgb(132, 0, 39)', fontSize: '12px', textAlign: 'center', marginTop: '10px' }}>
              * Cancelamentos só são permitidos com no mínimo 24h de antecedência. Agendamentos com menos de 24h serão cobrados.
            </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CalendarGrid;
